import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "store";
import {
  MenuHome,
  MenuHistory,
  MenuRunway,
  MenuSocial,
  MenuTrendHistory,
  MenuMoodboards,
  MenuMyMoodboards,
  MenuRunwayMoodboards,
  MenuBrandCatalogues,
  MenuDesignCanvas,
  MenuDesignAssets,
  MenuPhotoshootAssets,
  MenuFabrics,
  MenuPrints,
  MenuSketches,
  MenuImages,
  MenuColorPalettes,
  MenuBrandKits,
  MenuPhotoshootStudio,
  MenuProducts,
  MenuBackgrounds,
  MenuPoses,
  MenuModel,
  MenuTechPack,
  MenuBulkProcess,
  MenuIntegrations,
} from "icons/menuIcons";
import { ChartScatter, FileChartColumn, Folder, MessageSquare, TrendingUp } from "lucide-react";
import { PATHS } from "router/config/paths";
import { withFitModelsUnderTechPack } from "./fitModelsMenu";
import { AnalyticsEvent } from "utils";
import { isModuleEnabled, type ModulePermissions } from "types/permissions";
import { confirmNavigation } from "utils/unsavedChangesGuard";
import { useAgentThreads } from "api/agentChat/useAgentThreads";

export interface MenuItem {
  /** Stable id — used for expand/collapse state and React keys (labels are localized, so not stable). */
  key: string;
  name: string;
  /** Navigation target. A pure container (rare) may omit it and only expand. */
  path?: string;
  /** Menu icon (IconPark-backed wrapper from `icons/menuIcons`). Accepts a `className`. */
  icon?: React.ComponentType<{ className?: string }>;
  isVisible?: boolean;
  analyticsEvent?: string;
  children?: MenuItem[];
  /** Match the current route by exact pathname only (no `startsWith` prefix). Used for the
   *  "all" asset parent so it doesn't light up on its own subtype children's routes. */
  exact?: boolean;
  /** Dropdown rows only: clicking navigates to this group's OWN `path` (e.g. Moodboards → /moodboards)
   *  instead of its first child. Default (unset) selects the first sub-item, so the child highlights. */
  openSelf?: boolean;
  /** Extra routes that should also light this item up (same prefix rule as `path`). Used for a
   *  sub-state that lives on its own route but belongs to this item — e.g. My Moodboards staying
   *  selected while the Shared-With-Me view (/moodboards/shared) is open. */
  matchPaths?: string[];
  /** Overrides route matching when the item's identity is in the query string (a chat is
   *  `/design-canvas-agent?thread=…`, every chat shares the pathname). */
  active?: boolean;
  /** A group that only opens and closes (More): its row navigates nowhere. */
  toggleOnly?: boolean;
  /** A second, quieter line under the name (a recent chat's canvas). */
  subtitle?: string;
}

export interface MenuSection {
  index: number;
  title?: string;
  items: MenuItem[];
  /** On the collapsed rail the section is this one row (its items in the flyout) — items with no icon of their own. */
  collapsedItem?: MenuItem;
  /** Its rows can be searched by name from the section's header (recent chats). */
  searchable?: boolean;
}

/** How many conversations the main menu lists; the chat's own history menu has them all. */
const RECENT_CHATS = 10;

/**
 * Single source of truth for the primary navigation: the nested menu tree (with permission-based
 * visibility), the active-route matchers, and the navigation handler. Consumed by both the desktop
 * `SideMenu` and the mobile nav drawer so the two never drift on items, ordering, or gates.
 *
 * The tree is four sections (the chat agent's sidebar): Home and Assets (Design Assets, Photoshoot Assets,
 * Integrations, History); Moodboards and Trends (Runway Trends, Runway Data, Social Media, Retail Brands); Explore Tools (Design
 * Canvas, Photoshoot Studio, Tech Pack, Bulk Process); Recent Chats. A group row with a path opens it; Assets and
 * Trends open their first link; Reports sits beside them when a report is shared with the team. Developers is in the
 * avatar menu (`isApiUsageVisible`).
 */
/**
 * The Developers section (API keys, webhooks, jobs, usage) opens from the avatar menu, not the rail: it is
 * the account's, not a place to work in. Shown only when the plan grants API access (`api_usage`, K98).
 */
export const isApiUsageVisible = (perms: ModulePermissions | undefined): boolean => perms?.api_usage?.is_visible === true && perms?.api_usage?.is_enabled === true;

export const useMenuItems = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, isAuthorized } = useAppSelector((state) => state.common);

  const perms = user.permissions;
  const trendsVisible = perms?.trends?.is_visible !== false;
  const brandCatalogueVisible = perms?.brand_catalogue?.is_visible === true && perms?.brand_catalogue?.is_enabled === true;
  const designVisible = perms?.design?.is_visible === true;
  const photoshootVisible = perms?.photoshoot?.is_visible !== false;
  // Runway Trends / Runway Data are `trends` (the entitlement rules book, 2026-11-02). Shown where it is VISIBLE: a
  // no-plan account (visible, not enabled — the default template) sees the rows and meets the upgrade wall on entry.
  const runwayVisible = perms?.trends?.is_visible === true;
  // Retail Trends / Retail Data are `brand_catalogue` (`brandCatalogueVisible`), as the BE's retail routes ask.
  // Reports (REF-7271) are shared with a team, not granted by the plan: the row shows when `GET /v1/reports` listed one.
  // Optional chain: tests that mock `store` with a partial state (runwayAccess.test) carry no `reports` slice.
  const reportsVisible = useAppSelector((state) => (state.reports?.items.length ?? 0) > 0);

  // The design agent's chat is a beta grant (`agent_chat`) — the same predicate as its route guard. Without it there is
  // no chat anywhere in the menu: no recent chats, no new chat. The canvases stay (Design Canvas).
  const agentChatEnabled = isModuleEnabled("agent_chat", perms, user.packageCode);
  // Recent chats with the design agent, under the asset libraries. Same gate as the agent page itself.
  const agentThreads = useAgentThreads({ enabled: agentChatEnabled && isAuthorized });
  const openThreadId = location.pathname === PATHS.DESIGN_CANVAS_AGENT ? new URLSearchParams(location.search).get("thread") : null;
  // Recent chats, by their own title alone (2026-10-20: no canvas line under each — the chat's page shows its canvas).
  const recentChats: MenuItem[] = agentThreads.threads.slice(0, RECENT_CHATS).map((thread) => ({
    key: `chat-${thread.threadId}`,
    name: thread.title || t("home:designCanvasAgent.threads.untitled"),
    path: `${PATHS.DESIGN_CANVAS_AGENT}?${new URLSearchParams({ ...(thread.canvasId ? { id: thread.canvasId } : {}), thread: thread.threadId })}`,
    active: thread.threadId === openThreadId,
  }));

  // The rail, top to bottom (the chat agent's own sidebar, 2026-10-16): Home and Assets; Moodboards and Trends;
  // the tools; then the conversations. Every row keeps its key, route and gate — only where it sits changed.
  const sections: MenuSection[] = [
    {
      index: 0,
      items: [
        { key: "home", name: t("common:nav.home"), path: PATHS.HOME, icon: MenuHome, exact: true, analyticsEvent: "nav_home_click" },
        {
          key: "assets",
          name: t("common:nav.my_assets"),
          icon: Folder,
          children: [
            {
              key: "assets-brand-kits",
              name: t("common:nav.brand_kits"),
              path: PATHS.DESIGN_ASSETS_BRAND_KITS,
              icon: MenuBrandKits,
              isVisible: designVisible,
              analyticsEvent: "nav_assets_brandkits_click",
            },
            {
              key: "design-assets",
              name: t("common:nav.design_assets"),
              path: `${PATHS.DESIGN_ASSETS}/fabric`,
              icon: MenuDesignAssets,
              exact: true,
              isVisible: designVisible,
              analyticsEvent: "nav_myassets_click",
              children: [
                { key: "assets-fabric", name: t("common:nav.fabrics"), path: `${PATHS.DESIGN_ASSETS}/fabric`, icon: MenuFabrics, analyticsEvent: "nav_assets_fabric_click" },
                { key: "assets-print", name: t("common:nav.prints"), path: `${PATHS.DESIGN_ASSETS}/print`, icon: MenuPrints, analyticsEvent: "nav_assets_print_click" },
                { key: "assets-sketch", name: t("common:nav.sketches"), path: `${PATHS.DESIGN_ASSETS}/sketch`, icon: MenuSketches, analyticsEvent: "nav_assets_sketch_click" },
                { key: "assets-other", name: t("common:nav.others"), path: `${PATHS.DESIGN_ASSETS}/other`, icon: MenuImages, analyticsEvent: "nav_assets_other_click" },
                {
                  key: "assets-color-palettes",
                  name: t("common:nav.color_palettes"),
                  path: PATHS.DESIGN_ASSETS_COLOR_PALETTES,
                  icon: MenuColorPalettes,
                  analyticsEvent: "nav_assets_colorpalettes_click",
                },
              ],
            },
            {
              key: "photoshoot-assets",
              name: t("common:nav.photoshoots_assets"),
              path: `${PATHS.PHOTOSHOOT_ASSETS}/products`,
              icon: MenuPhotoshootAssets,
              exact: true,
              isVisible: photoshootVisible,
              analyticsEvent: "nav_myassets_click",
              children: [
                { key: "assets-products", name: t("common:nav.products"), path: `${PATHS.PHOTOSHOOT_ASSETS}/products`, icon: MenuProducts, exact: true, analyticsEvent: "nav_assets_products_click" },
                { key: "assets-background", name: t("common:nav.backgrounds"), path: `${PATHS.PHOTOSHOOT_ASSETS}/backgrounds`, icon: MenuBackgrounds, analyticsEvent: "nav_assets_background_click" },
                { key: "assets-pose", name: t("common:nav.poses"), path: `${PATHS.PHOTOSHOOT_ASSETS}/poses`, icon: MenuPoses, analyticsEvent: "nav_assets_pose_click" },
                { key: "assets-models", name: t("common:nav.models"), path: PATHS.PHOTOSHOOT_ASSETS_MODELS, icon: MenuModel, analyticsEvent: "nav_assets_models_click" },
              ],
            },
            { key: "history", name: t("common:nav.history"), path: PATHS.HISTORY, icon: MenuHistory, analyticsEvent: "nav_history_click" },
            { key: "integrations", name: t("common:nav.integrations"), path: PATHS.INTEGRATIONS, icon: MenuIntegrations, analyticsEvent: "nav_integrations_click" },
          ],
        },
      ],
    },
    {
      index: 1,
      items: [
        {
          key: "moodboards",
          name: t("common:nav.moodboards"),
          path: PATHS.MOODBOARDS,
          icon: MenuMoodboards,
          exact: true,
          openSelf: true,
          isVisible: trendsVisible || brandCatalogueVisible,
          analyticsEvent: "nav_trendreports_click",
          children: [
            {
              key: "moodboards-my",
              name: t("common:nav.my_moodboards"),
              path: PATHS.MOODBOARDS_MY,
              icon: MenuMyMoodboards,
              isVisible: trendsVisible,
              // The Shared-With-Me view lives on its own route but is reached from My Moodboards, so
              // keep this item selected there instead of dropping the highlight (REF-6157).
              matchPaths: [PATHS.MOODBOARDS_SHARED],
              analyticsEvent: "nav_moodboards_my_click",
            },
            {
              key: "moodboards-runway",
              name: t("common:nav.runway_moodboards"),
              path: PATHS.MOODBOARDS_RUNWAY,
              icon: MenuRunwayMoodboards,
              isVisible: trendsVisible,
              analyticsEvent: "nav_moodboards_runway_click",
            },
            {
              key: "moodboards-brand-catalogues",
              name: t("common:nav.brand_catalogues"),
              path: PATHS.MOODBOARDS_BRAND_CATALOGUES,
              icon: MenuBrandCatalogues,
              isVisible: brandCatalogueVisible,
              analyticsEvent: "nav_moodboards_brandcatalogues_click",
            },
          ],
        },
        {
          key: "trends",
          name: t("common:nav.trends"),
          icon: TrendingUp,
          isVisible: trendsVisible || brandCatalogueVisible || runwayVisible,
          children: [
            // Runway Trends, then its data, then the other sources (2026-10-23). Runway (`/discover/runway`) is hidden
            // for now, not removed: Runway Trends stands in its place. Trend History gives way to Runway Trends too.
            // String literals like their siblings (gen-capability-graph reads them). EVENTS.NAV.RUNWAY_TRENDS.
            {
              key: "runway-trends",
              name: t("common:nav.runway_trends"),
              path: PATHS.TREND_HISTORY_V2,
              icon: MenuRunway,
              isVisible: runwayVisible,
              analyticsEvent: "nav_runway_trends_click",
            },
            // Runway Data reads the trend-history tree, behind the runway grant (`trends`). EVENTS.NAV.DISCOVER_HISTORY.
            {
              key: "discover-history",
              name: t("common:nav.runway_data"),
              path: PATHS.TRENDS_HISTORY,
              // Its page is a map of the season's garment categories as clusters of looks: dots in clusters.
              icon: ChartScatter,
              isVisible: runwayVisible,
              analyticsEvent: "nav_discover_history_click",
            },
            // Retail Trends (REF-7160): Runway Trends' finish over the brand catalogue's arrivals. EVENTS.NAV.RETAIL_TRENDS.
            {
              key: "retail-trends",
              name: t("common:nav.retail_trends"),
              path: PATHS.RETAIL_TRENDS,
              icon: MenuBrandCatalogues,
              isVisible: brandCatalogueVisible,
              analyticsEvent: "nav_retail_trends_click",
            },
            // Retail Data (2026-10-28): Runway Data's map on the catalogue — the same grants as Retail Trends.
            // EVENTS.NAV.RETAIL_DATA.
            {
              key: "retail-data",
              name: t("common:nav.retail_data"),
              path: PATHS.RETAIL_DATA,
              icon: ChartScatter,
              isVisible: brandCatalogueVisible,
              analyticsEvent: "nav_retail_data_click",
            },
            { key: "discover-social", name: t("common:nav.social_media"), path: PATHS.DISCOVER_SOCIAL, icon: MenuSocial, isVisible: trendsVisible, analyticsEvent: "nav_discover_social_click" },
            { key: "discover-runway", name: t("common:nav.runway"), path: PATHS.DISCOVER_RUNWAY, icon: MenuRunway, isVisible: false, analyticsEvent: "nav_discover_runway_click" },
            {
              key: "trend-history",
              name: t("common:nav.trend_history"),
              path: PATHS.TREND_HISTORY,
              icon: MenuTrendHistory,
              isVisible: false,
              analyticsEvent: "nav_trend_history_click",
            },
          ],
        },
      ],
    },
    {
      index: 2,
      title: t("common:nav.explore_tools"),
      items: [
        { key: "design-canvas", name: t("common:nav.design_canvas"), path: PATHS.CANVAS_PROJECTS, icon: MenuDesignCanvas, isVisible: designVisible, analyticsEvent: "nav_designcanvas_click" },
        {
          key: "photoshoot-studio",
          name: t("common:nav.photoshoot_studio"),
          path: PATHS.PHOTOSHOOTS_STUDIO,
          icon: MenuPhotoshootStudio,
          isVisible: photoshootVisible,
          analyticsEvent: "nav_photoshootstudio_click",
        },
        { key: "tech-pack", name: t("common:nav.tech_pack"), path: PATHS.TECH_PACKS, icon: MenuTechPack, isVisible: perms?.design?.is_visible !== false, analyticsEvent: "nav_techpack_click" },
        // Bulk Process on its own row; the More group and Collection Range Plan left the menu (owner, 2026-10-22). The
        // range plans keep their page and route: only the menu stopped listing them.
        {
          key: "bulk-process",
          name: t("common:nav.bulk_process"),
          path: PATHS.MARKETPLACE_STUDIO,
          icon: MenuBulkProcess,
          isVisible: perms?.artistic_sketch?.is_visible !== false,
          analyticsEvent: "nav_bulkprocess_click",
        },
      ],
    },
    // Recent chats are a section of their own: the conversations are a place the user returns to, not an
    // asset library. No section when there is none (or the agent is not in the plan).
    {
      index: 3,
      title: t("common:nav.recent_chats"),
      items: agentChatEnabled ? recentChats : [],
      searchable: true,
      collapsedItem: { key: "recent-chats", name: t("common:nav.recent_chats"), icon: MessageSquare, children: recentChats },
    },
  ];

  /* Fit Models sits under Tech Pack (shipped on, no env flag — 2026-10-14). Added here, outside the `sections` literal
   * that scripts/gen-capability-graph.mjs parses as the chatbot's destinations: its feature doc is still held out of
   * the chatbot corpus, so the chatbot is not sent there yet. */
  const withFitModels = withFitModelsUnderTechPack(sections, { techPacks: t("common:nav.tech_pack"), fitModels: t("common:nav.fit_models") });

  /* Reports (REF-7271) sit beside Moodboards and Trends, for a team a report is shared with. Added outside the `sections`
   * literal for the same reason as Fit Models: its feature doc is held out of the chatbot corpus until the first
   * report's page ships (REF-7272), and its gate is a team share, not a permission the graph can express.
   * EVENTS.NAV.REPORTS. */
  const reportsItem: MenuItem = { key: "reports", name: t("common:nav.reports"), path: PATHS.REPORTS, icon: FileChartColumn, isVisible: reportsVisible, analyticsEvent: "nav_reports_click" };
  const menuSections = withFitModels.map((section) => (section.index === 1 ? { ...section, items: [...section.items, reportsItem] } : section));

  // Recursively drop hidden nodes. A node stays if it isn't explicitly hidden; a group whose
  // children all disappear keeps its own row (it still opens its landing page).
  const filterVisible = React.useCallback((items: MenuItem[]): MenuItem[] => {
    return items.filter((item) => item.isVisible === undefined || item.isVisible).map((item) => (item.children ? { ...item, children: filterVisible(item.children) } : item));
  }, []);

  const visibleSections = React.useMemo(
    () => menuSections.map((section) => ({ ...section, items: filterVisible(section.items) })).filter((section) => section.items.length > 0),
    // sections is rebuilt each render from perms/translation; depend on the primitives that drive it.
    [filterVisible, trendsVisible, brandCatalogueVisible, runwayVisible, reportsVisible, designVisible, photoshootVisible, agentChatEnabled, perms, t, agentThreads.threads, openThreadId]
  );

  // This node's own destination is the current page (leaf highlight).
  const isExactActive = React.useCallback(
    (item: Pick<MenuItem, "path" | "exact" | "matchPaths" | "active">) => {
      if (item.active !== undefined) return item.active;
      // A sub-state route (e.g. /moodboards/shared) can keep its owning item lit.
      if (item.matchPaths?.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"))) return true;
      if (!item.path) return false;
      if (item.path === "/") return location.pathname === "/";
      if (item.exact) return location.pathname === item.path;

      return location.pathname === item.path || location.pathname.startsWith(item.path + "/");
    },
    [location.pathname]
  );

  // This node or any descendant is active (parent trail highlight + default-expanded branch).
  const isBranchActive = React.useCallback((item: MenuItem): boolean => isExactActive(item) || (item.children?.some(isBranchActive) ?? false), [isExactActive]);

  const handleItemClick = async (item: MenuItem) => {
    if (!item.path) return;
    if (!isAuthorized && item.path !== PATHS.HOME) return;
    if (!(await confirmNavigation())) return;
    if (item.analyticsEvent) {
      AnalyticsEvent(item.analyticsEvent, "", "click");
    }
    navigate(item.path);
  };

  // A new conversation with the design agent: the agent page, fresh (it keys a new instance on `newChat`).
  const startNewChat = async () => {
    if (!(await confirmNavigation())) return;
    navigate(PATHS.DESIGN_CANVAS_AGENT, { state: { newChat: Date.now() } });
  };

  return { sections: visibleSections, isExactActive, isBranchActive, handleItemClick, isAuthorized, newChat: agentChatEnabled && isAuthorized ? startNewChat : null };
};
